import { CtOrder, CtPacking, PackingItem } from '@/models/interfaces/content-type'
import { ProductSelection, ReplacementInstruction, OrderListProductState, ProductDragState, ProductView, ReplacementType } from '../interfaces'
import { useStore } from 'vuex'
import { notEmpty } from '@/utils'
import { computed } from 'vue'
import { Status } from '@/models/status'
import _ from 'lodash'

const cancelableStatus = [Status.New, Status.Pending, Status.Failed, Status.Paused, Status.Queued, Status.Starting, Status.Active, Status.Changed]
const replaceableStatus = [Status.Stopped, Status.Resolved, Status.Final, Status.Cancelled]
const replacementReasons = [
  'Quality issue',
  'Wrong product',
  'Missing product',
  'Product damaged',
  'Product damaged during delivery',
  'Product perished during delivery',
  'Shipment address issue (not delivered)',
  'Shipment carrier did not deliver on time',
  'Shipment carrier failed to deliver',
  'Shipment carrier unjustified return'
]

export default function useProductReplacement (props, productState, parsedPackages, startingState, emitPacking, emitEdit, emitReplace) {

  const $store = useStore()
  const $directr = $store.getters['Services/Directr']

  const shouldCreateReplacement = computed(() => (props.status || []).some(status => replaceableStatus.includes(status)))

  function handleCancelandReplace () {
    productState.edit = true
    emitEdit(true)
  }

  function abortCancelandReplace () {
    emitPacking(_.cloneDeep([...startingState.value])) // re-emits the starting state stored on mount
    productState.edit = false
    productState.diff = false
    emitEdit(false)
    // revert any changes made

    productState.selection = []
    productState.show = false
    productState.replace.products = []
  }

  async function commitChanges () {
    try {
      if (productState.diff || parsedPackages.value.some(p => p.draft)) await commitCustomPacking()
      if (shouldCreateReplacement.value || (productState.replace.products?.length || productState.selection?.length)) await commitCancelandReplace()
    } catch (e) {
      console.error('Error committing changes', e)
    } finally {
      productState.edit = false
      emitEdit(false)
    }
  }

  async function commitCustomPacking () {
    try {
      const updated = await $directr.order.patchPacking(props.id, parsedPackages.value.map(p => { delete p.draft; return p }))
      return updated
    } catch (e) {
      console.error('Error committing custom packing', e)
    } finally {
      productState.loading = false
      productState.edit = false
      productState.diff = false
      emitEdit(false)
    }
  }

  // commit changes
  async function commitCancelandReplace () {
    try {
      productState.loading = true
      // productState.replace.products = selectionState
      // it is separated since the generic selectionState also serves as the partial-fulfillment selection

      // NOTE if state.shouldCreateReplacement is true, the replace items will not be included in the new order
      const replace = (productState.replace.products || []).map(p => p.id)

      const ri:ReplacementInstruction = {
        cancel: replace,
        replacements: productState.selection.map(s => s.sys?.id ? { id: s.sys.id, quantity: s.quantity } : null).filter(notEmpty),
        reason: productState.replace.reason,
        type: productState.replace.type?.value
      }

      // if order status is eligable, create new replacement order, otherwise simply update the current order
      if (props.id) {
        const { items } = await $directr.order.replace(props.id, ri, { create: shouldCreateReplacement.value })
        console.log
        emitReplace(items[0])
      }
      else console.warn('Tried to commit product changes, but no order id was set on the product-list')

      productState.selection = []
    } catch (e) {
      console.error(e)
    } finally {
      productState.loading = false
      productState.edit = false
      productState.diff = false
      emitEdit(false)
    }
  }

  function handleProductSelect ({ target, selection }) {
    try {
      if (!$directr) {
        console.warn('ProductList.handleProductSelect was called but no request prop was set')
        return
      }

      productState.show = false
      productState.selection = [...selection.map(s => ({ ...s, quantity: 1 })), ...productState.selection.map(s => ({ ...s, quantity: 1 }))]
    } catch (e) {
      console.error('Error during product selection handling', e)
    } finally {
      // reset state
      productState.show = false
      // productState.replace.products = null
    }
  }

  function searchProductItem () {
    productState.show = true
  }

  return {
    productState,
    handleCancelandReplace,
    abortCancelandReplace,
    commitChanges,
    commitCustomPacking,
    commitCancelandReplace,
    handleProductSelect,
    searchProductItem,
    shouldCreateReplacement
  }
}
